import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  LabelContent,
  HeadTableCell,
  RowTableCell,
  CustomTextField,
  ConfirmationDialog,
} from '../../../common';
import { useUpdateCoupon } from '../../../hooks/useCoupons';

const EditCouponDialog = ({
  open,
  onClose,
  selectedData: initialData,
  calculateInterest,
}) => {
  const [selectedData, setSelectedData] = useState(null);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const updateCouponMutation = useUpdateCoupon();
  const isSaving = updateCouponMutation.isPending;
  const itemKeys = ['pei', 'social_fee', 'life_insurance', 'fire_insurance', 'property_tax'];

  useEffect(() => {
    if (open && initialData) {
      setSelectedData({ ...initialData });
    } else if (!open) {
      setSelectedData(null);
      setConfirmSaveOpen(false);
    }
  }, [open, initialData]);

  const toWholeNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(numericValue)) return 0;
    return Math.round(numericValue);
  };

  const formatWhole = (value) => toWholeNumber(value).toString();

  const normalizeDate = (value) => {
    if (!value) return '';
    return String(value).slice(0, 10);
  };

  const hasQrSensitiveChanges = () => {
    if (!initialData || !selectedData) return false;

    const dateFields = ['first_due_date', 'second_due_date', 'third_due_date'];
    const amountFields = ['first_due_amount', 'second_due_amount', 'third_due_amount'];

    const datesChanged = dateFields.some(
      (field) => normalizeDate(initialData[field]) !== normalizeDate(selectedData[field])
    );

    const amountsChanged = amountFields.some(
      (field) => toWholeNumber(initialData[field]) !== toWholeNumber(selectedData[field])
    );

    return datesChanged || amountsChanged;
  };

  const recalculateDueAmounts = (draft) => {
    const next = {
      ...draft,
      items: draft.items ? { ...draft.items } : {},
    };

    const feeMountNumber = toWholeNumber(next.fee_mount);
    const itemsTotal = itemKeys.reduce((sum, key) => sum + toWholeNumber(next.items?.[key]), 0);
    const firstDueAmountNumber = feeMountNumber + itemsTotal;

    next.first_due_amount = formatWhole(firstDueAmountNumber);
    next.first_interest = formatWhole(next.first_interest);

    const buildInterest = (targetDate) => {
      const interestData = calculateInterest(
        firstDueAmountNumber,
        next.first_due_date,
        targetDate
      );
      return {
        interest: formatWhole(interestData.interest),
        total: formatWhole(interestData.total),
      };
    };

    if (next.first_due_date && next.second_due_date) {
      const secondInterest = buildInterest(next.second_due_date);
      next.second_interest = secondInterest.interest;
      next.second_due_amount = secondInterest.total;
    } else {
      const secondInterestNumber = toWholeNumber(next.second_interest);
      next.second_interest = formatWhole(secondInterestNumber);
      next.second_due_amount = formatWhole(firstDueAmountNumber + secondInterestNumber);
    }

    if (next.first_due_date && next.third_due_date) {
      const thirdInterest = buildInterest(next.third_due_date);
      next.third_interest = thirdInterest.interest;
      next.third_due_amount = thirdInterest.total;
    } else {
      const thirdInterestNumber = toWholeNumber(next.third_interest);
      next.third_interest = formatWhole(thirdInterestNumber);
      next.third_due_amount = formatWhole(firstDueAmountNumber + thirdInterestNumber);
    }

    return next;
  };

  const setEditCouponField = ({ key, value, nestedKey, shouldRecalculate = false }) => {
    setSelectedData(prev => {
      if (!prev) return prev;

      const updated = nestedKey
        ? {
            ...prev,
            [nestedKey]: {
              ...(prev[nestedKey] || {}),
              [key]: value,
            },
          }
        : {
            ...prev,
            [key]: value,
          };

      if (shouldRecalculate) {
        return recalculateDueAmounts(updated);
      }

      return updated;
    });
  };

  const persistChanges = async () => {
    if (!selectedData || isSaving) return;

    const buildItemsPayload = () => {
      const items = selectedData.items || {};
      return Object.entries(items).reduce((acc, [key, value]) => {
        acc[key] = key.startsWith('uuid_') ? value : toWholeNumber(value);
        return acc;
      }, {});
    };

    const payload = {
      first_due_amount: toWholeNumber(selectedData.first_due_amount),
      second_due_amount: toWholeNumber(selectedData.second_due_amount),
      third_due_amount: toWholeNumber(selectedData.third_due_amount),
      first_interest: toWholeNumber(selectedData.first_interest),
      second_interest: toWholeNumber(selectedData.second_interest),
      third_interest: toWholeNumber(selectedData.third_interest),
      fee_mount: toWholeNumber(selectedData.fee_mount),
      items: buildItemsPayload(),
      first_due_date: selectedData.first_due_date,
      second_due_date: selectedData.second_due_date,
      third_due_date: selectedData.third_due_date,
    };

    try {
      await updateCouponMutation.mutateAsync({
        id: selectedData.uuid,
        data: payload,
      });
      setConfirmSaveOpen(false);
      onClose();
    } catch (error) {
      console.error('Error updating coupon:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedData || isSaving) return;

    if (hasQrSensitiveChanges()) {
      setConfirmSaveOpen(true);
      return;
    }

    await persistChanges();
  };

  const handleConfirmSave = async () => {
    await persistChanges();
  };

  const dueMapping = [
    {
      label: '1er Vto.:',
      amount: 'first_due_amount',
      date: 'first_due_date',
      interest: 'first_interest',
    },
    {
      label: '2do Vto.:',
      amount: 'second_due_amount',
      date: 'second_due_date',
      interest: 'second_interest',
    },
    {
      label: '3er Vto.:',
      amount: 'third_due_amount',
      date: 'third_due_date',
      interest: 'third_interest',
    },
  ];

  const handleInputChange = (e) => {
    const { name, value: rawValue } = e.target;
    const value = formatWhole(rawValue);

    if (selectedData?.items && name in selectedData.items) {
      setEditCouponField({
        key: name,
        value: value,
        nestedKey: 'items',
        shouldRecalculate: true,
      });
      return;
    }

    if (name === 'fee_mount') {
      setEditCouponField({
        key: name,
        value: value,
        shouldRecalculate: true,
      });
      return;
    }

    if (name === 'second_interest') {
      setSelectedData(prev => {
        if (!prev) return prev;
        const secondInterest = toWholeNumber(value);
        const firstAmountNumber = toWholeNumber(prev.first_due_amount);
        const updatedSecondDue = firstAmountNumber + secondInterest;

        return {
          ...prev,
          second_interest: secondInterest.toString(),
          second_due_amount: updatedSecondDue.toString(),
        };
      });
      return;
    }

    if (name === 'third_interest') {
      setSelectedData(prev => {
        if (!prev) return prev;
        const thirdInterest = toWholeNumber(value);
        const firstAmountNumber = toWholeNumber(prev.first_due_amount);
        const updatedThirdDue = firstAmountNumber + thirdInterest;

        return {
          ...prev,
          third_interest: thirdInterest.toString(),
          third_due_amount: updatedThirdDue.toString(),
        };
      });
      return;
    }

    setEditCouponField({ key: name, value: value });
  };

  const handleDateChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setSelectedData(prev => {
      if (!prev) return prev;
      const next = {
        ...prev,
        [name]: value,
      };
      return recalculateDueAmounts(next);
    });
  };

  if (!open || !selectedData || !selectedData.uuid) return null;

  return (
    <DialogContainer open={open} onClose={onClose} size="large">
      <DialogTitle>Editar Vencimiento</DialogTitle>

      <DialogBody>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <LabelContent label="Contrato" content={selectedData?.orden} labelColor="primary.main" />
          <LabelContent label="Plan" content={selectedData?.plan?.name} labelColor="primary.main" />
          <LabelContent label="Cuota/s" content={selectedData?.cuotas} labelColor="primary.main" />
        </Box>

        <Table sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <HeadTableCell wmd="7%" />
                <HeadTableCell />
                <HeadTableCell>Interes</HeadTableCell>
                <HeadTableCell>Cuota</HeadTableCell>
                {selectedData?.items?.uuid_pei && <HeadTableCell>PEI</HeadTableCell>}
                {selectedData?.items?.uuid_social_fee && (
                  <HeadTableCell>Cuota Social</HeadTableCell>
                )}
                {selectedData?.items?.uuid_life_insurance && (
                  <HeadTableCell>Seguro de Vida</HeadTableCell>
                )}
                {selectedData?.items?.uuid_fire_insurance && (
                  <HeadTableCell>Seguro de Incendio</HeadTableCell>
                )}
                {selectedData?.items?.uuid_property_tax && (
                  <HeadTableCell>Impuesto Inmobiliario</HeadTableCell>
                )}
                <HeadTableCell />
              </TableRow>
            </TableHead>
            <TableBody sx={{ alignItems: 'center' }}>
              {dueMapping.map((due, rowIndex) => (
                <TableRow key={rowIndex}>
                  <RowTableCell>{due.label}</RowTableCell>
                  <RowTableCell>
                    <CustomTextField
                      sx={{ mt: 0 }}
                      size="small"
                      fullWidth
                      name={due.amount}
                      type="text"
                      disabled
                      value={selectedData[due.amount]}
                      onChange={handleInputChange}
                    />
                  </RowTableCell>
                  <RowTableCell>
                    <CustomTextField
                      sx={{ mt: 0 }}
                      size="small"
                      fullWidth
                      name={due.interest}
                      type="text"
                      value={selectedData[due.interest]}
                      disabled={rowIndex === 0}
                      onChange={handleInputChange}
                    />
                  </RowTableCell>
                  <RowTableCell>
                    <CustomTextField
                      sx={{ mt: 0 }}
                      size="small"
                      fullWidth
                      name="fee_mount"
                      type="text"
                      value={selectedData.fee_mount}
                      disabled={rowIndex !== 0}
                      onChange={handleInputChange}
                    />
                  </RowTableCell>
                  {selectedData?.items?.uuid_pei && (
                    <RowTableCell>
                      <CustomTextField
                        sx={{ mt: 0 }}
                        size="small"
                        fullWidth
                        name="pei"
                        type="text"
                        value={selectedData?.items?.pei || ''}
                        disabled={rowIndex !== 0}
                        onChange={handleInputChange}
                      />
                    </RowTableCell>
                  )}
                  {selectedData?.items?.uuid_social_fee && (
                    <RowTableCell>
                      <CustomTextField
                        sx={{ mt: 0 }}
                        size="small"
                        fullWidth
                        name="social_fee"
                        type="text"
                        value={selectedData?.items?.social_fee || ''}
                        disabled={rowIndex !== 0}
                        onChange={handleInputChange}
                      />
                    </RowTableCell>
                  )}
                  {selectedData?.items?.uuid_life_insurance && (
                    <RowTableCell>
                      <CustomTextField
                        sx={{ mt: 0 }}
                        size="small"
                        fullWidth
                        name="life_insurance"
                        type="text"
                        value={selectedData?.items?.life_insurance || ''}
                        disabled={rowIndex !== 0}
                        onChange={handleInputChange}
                      />
                    </RowTableCell>
                  )}
                  {selectedData?.items?.uuid_fire_insurance && (
                    <RowTableCell>
                      <CustomTextField
                        sx={{ mt: 0 }}
                        size="small"
                        fullWidth
                        name="fire_insurance"
                        type="text"
                        value={selectedData?.items?.fire_insurance || ''}
                        disabled={rowIndex !== 0}
                        onChange={handleInputChange}
                      />
                    </RowTableCell>
                  )}
                  {selectedData?.items?.uuid_property_tax && (
                    <RowTableCell>
                      <CustomTextField
                        sx={{ mt: 0 }}
                        size="small"
                        fullWidth
                        name="property_tax"
                        type="text"
                        value={selectedData?.items?.property_tax || ''}
                        disabled={rowIndex !== 0}
                        onChange={handleInputChange}
                      />
                    </RowTableCell>
                  )}
                  <RowTableCell>
                    <CustomTextField
                      sx={{ mt: 0 }}
                      name={due.date}
                      type="date"
                      size="small"
                      value={selectedData[due.date] ? selectedData[due.date].substr(0, 10) : ''}
                      disabled={rowIndex === 0}
                      onChange={handleDateChange}
                    />
                  </RowTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Table>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSave}
        saveButtonProps={{ isLoading: isSaving }}
      />

      <ConfirmationDialog
        open={confirmSaveOpen}
        onClose={() => setConfirmSaveOpen(false)}
        onConfirm={handleConfirmSave}
        title="Confirmar regeneración de QR"
        message={
          'Al cambiar fechas o importes se regenerarán los QR del cupón. ' +
          'Los QR anteriores dejarán de impactar pagos y el PDF/impresión vieja quedará desactualizado.'
        }
        details={
          'Si el suscriptor paga con un QR o código de barras de un cupón descargado antes de este cambio, ' +
          'el cobro puede entrar con datos viejos o no impactar correctamente.'
        }
        entityName="cupones"
        confirmText="Guardar cambios"
        cancelText="Volver"
        confirmVariant="warning"
        isLoading={isSaving}
      />
    </DialogContainer>
  );
};

export default EditCouponDialog;
